import { useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Camera } from "lucide-react";
import { useLanguage } from "@/hooks/useLanguage";
import { useMode } from "@/hooks/useMode";
import { useIsDemoMode } from "@/hooks/useDemoMode";
import { setPendingCapture } from "@/lib/capture/pending";

/**
 * Phones only: a floating "Vyfotit účtenku" button on every business page.
 * It opens the camera straight away and hands the photo to the capture page,
 * where it is checked and uploaded.
 */
export function CaptureFab() {
  const { language } = useLanguage();
  const { mode } = useMode();
  // Sample data is read-only: an upload there is refused, so no camera button.
  const demo = useIsDemoMode();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const inputRef = useRef<HTMLInputElement>(null);

  if (demo || mode !== "business" || /^\/(cs|en)\/capture\/?$/.test(pathname)) return null;
  const label = language === "cs" ? "Vyfotit účtenku" : "Photograph a receipt";

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept="image/*,application/pdf"
        capture="environment"
        className="sr-only"
        tabIndex={-1}
        aria-hidden="true"
        data-testid="capture-fab-input"
        onChange={(e) => {
          const files = Array.from(e.target.files ?? []);
          e.target.value = "";
          if (files.length === 0) return;
          setPendingCapture(files);
          navigate(`/${language}/capture`);
        }}
      />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        className="fixed bottom-[calc(1rem+env(safe-area-inset-bottom))] right-4 z-40 inline-flex h-14 items-center gap-2 rounded-full bg-[hsl(var(--action))] pl-4 pr-5 text-[15px] font-medium text-[hsl(var(--action-foreground))] shadow-md transition-colors hover:bg-[hsl(var(--action)/0.9)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 dark:bg-primary dark:text-primary-foreground dark:hover:bg-primary/90 md:hidden"
      >
        <Camera className="h-5 w-5" aria-hidden="true" />
        {label}
      </button>
    </>
  );
}
